import { Box, Switch, TextField, Typography, Button } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";

const glassCard = { p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", border: "1px solid rgba(229,231,235,0.6)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } };
const accentText = (a) => ({ ...fieldSx, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: a }, "& .MuiInputLabel-root.Mui-focused": { color: a } });

const DEFAULT_FIELDS = {
  name: { enabled: true, required: true, label: "Full Name", placeholder: "Enter your full name" },
  email: { enabled: true, required: true, label: "Email", placeholder: "you@example.com" },
  phone: { enabled: false, required: false, label: "Phone", placeholder: "+91 98765 43210" },
};

const FIELD_DEFS = [
  { key: "name", label: "Name", icon: PersonIcon, color: "#6366f1" },
  { key: "email", label: "Email", icon: EmailIcon, color: "#06b6d4" },
  { key: "phone", label: "Phone", icon: PhoneIcon, color: "#10b981" },
];

const AskContactsTab = ({ skipOption, setSkipOption, errorMessage, setErrorMessage, settings = {}, setSettings, accentColor = "#ec4899" }) => {
  const fields = { ...DEFAULT_FIELDS, ...(settings.fields || {}) };
  const submitText = settings.submitText || "Submit";

  const updateField = (key, patch) => {
    setSettings({ ...settings, fields: { ...fields, [key]: { ...fields[key], ...patch } } });
  };

  const enabledCount = FIELD_DEFS.filter((f) => fields[f.key]?.enabled).length;

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Skip Option</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Allow visitors to skip contact capture</Typography>
          </Box>
          <Switch checked={!!skipOption} onChange={(e) => setSkipOption(e.target.checked)} size="small" />
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Contact Fields</Typography>
          <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
            Pick which contact details to ask. Visitors fill all enabled fields in one screen, then submit.
            {enabledCount > 0 && <Box component="span" sx={{ ml: 1, fontWeight: 700, color: accentColor }}>{enabledCount} field{enabledCount > 1 ? "s" : ""} selected</Box>}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {FIELD_DEFS.map((def) => {
            const Icon = def.icon;
            const f = fields[def.key];
            return (
              <Box key={def.key} sx={{ p: 1.5, bgcolor: f.enabled ? "#fff" : "#fafafa", border: `1.5px solid ${f.enabled ? accentColor : "#e5e7eb"}`, borderRadius: 2, opacity: f.enabled ? 1 : 0.7, transition: "all 0.2s" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: f.enabled ? 1 : 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: f.enabled ? `${def.color}15` : "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>
                      <Icon sx={{ fontSize: 18, color: f.enabled ? def.color : "#9ca3af" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}>{def.label}</Typography>
                      <Typography sx={{ fontSize: 10, color: "#6b7280" }}>{f.required ? "Required" : "Optional"}</Typography>
                    </Box>
                  </Box>
                  <Switch checked={!!f.enabled} onChange={(e) => updateField(def.key, { enabled: e.target.checked })} size="small" />
                </Box>

                {f.enabled && (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8 }}>
                    <TextField fullWidth size="small" label="Label" placeholder={def.label}
                      value={f.label || ""}
                      onChange={(e) => updateField(def.key, { label: e.target.value })}
                      sx={accentText(accentColor)} />
                    <TextField fullWidth size="small" label="Placeholder" placeholder={def.placeholder}
                      value={f.placeholder || ""}
                      onChange={(e) => updateField(def.key, { placeholder: e.target.value })}
                      sx={accentText(accentColor)} />
                    <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Switch checked={!!f.required} onChange={(e) => updateField(def.key, { required: e.target.checked })} size="small" />
                      <Typography sx={{ fontSize: 11, color: "#374151", fontWeight: 600 }}>Required field</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {enabledCount === 0 && (
          <Box sx={{ p: 1.5, mt: 1, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 1.5 }}>
            <Typography sx={{ fontSize: 11, color: "#dc2626", fontWeight: 600 }}>⚠ Enable at least one field above</Typography>
          </Box>
        )}
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1.5, color: "#1f2937" }}>Submit Button</Typography>
        <TextField fullWidth size="small" label="Button text" placeholder="Submit"
          value={submitText}
          onChange={(e) => setSettings({ ...settings, submitText: e.target.value })}
          sx={accentText(accentColor)} />
        <Typography sx={{ fontSize: 10, color: "#6b7280", mt: 0.8 }}>All values are saved as separate fields (name, email, phone) in the conversation.</Typography>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Error Message</Typography>
        <TextField fullWidth placeholder="Please fill all required fields" value={errorMessage || ""} size="small"
          onChange={(e) => setErrorMessage(e.target.value)}
          sx={fieldSx} />
      </Box>
    </Box>
  );
};

export default AskContactsTab;
